import {
  RecordType,
  importantDateIssues,
  sameJsonValue,
  normalizeLeadDays,
  type ImportantDateRecord,
  type ImportantDateValue,
  type RepeatKind,
} from '@wiztime/core';
import { ValidationError } from '../errors';
import type { WriteSession } from './session';

export interface ImportantDateInput {
  /** Omit to create a new important date. */
  readonly id?: string;
  readonly title: string;
  readonly date: ImportantDateValue;
  readonly repeat: RepeatKind;
  /** Days before an occurrence at which it is announced; stored distinct and ascending. */
  readonly leadDays: readonly number[];
  readonly note?: string | null;
  /** Omit to keep the stored value (true for a new important date). */
  readonly active?: boolean;
}

/**
 * Check that the date exists in its calendar (a lunar month or day the year really has). The
 * lunar library is loaded on demand — and before the command's database transaction starts,
 * because awaiting a module load inside an IndexedDB transaction can end the transaction.
 */
export async function assertImportantDateExists(input: Pick<ImportantDateInput, 'date'>): Promise<void> {
  const { isValidCalendarDate } = await import('@wiztime/core/calendar');
  if (!isValidCalendarDate(input.date)) {
    throw new ValidationError('The date does not exist in its calendar', [JSON.stringify(input.date)]);
  }
}

/** Create an important date, or update the live one with the same id. Unknown stored fields survive. */
export async function upsertImportantDate(
  session: WriteSession,
  input: ImportantDateInput,
): Promise<ImportantDateRecord> {
  const issues = importantDateIssues(input);
  if (issues.length > 0) throw new ValidationError('Invalid important date', issues);
  const existing = input.id === undefined ? null : await session.find(input.id);
  const current =
    existing !== null && existing.type === RecordType.ImportantDate && existing.data !== null
      ? existing.data
      : null;
  const note = input.note ?? null;
  const next = {
    ...current,
    title: input.title.trim(),
    date: { ...current?.date, ...input.date },
    repeat: input.repeat,
    leadDays: normalizeLeadDays(input.leadDays),
    note: note === null || note === '' ? null : note,
    active: input.active ?? current?.active ?? true,
  };
  // A save that changes nothing writes nothing: the last-updated time stays and nothing syncs.
  if (existing !== null && current !== null && sameJsonValue(next, current)) return existing as ImportantDateRecord;
  return session.write(RecordType.ImportantDate, input.id ?? session.newId(), next);
}

/** Deactivate or restore an important date; no write when nothing changes. */
export async function setImportantDateActive(
  session: WriteSession,
  id: string,
  active: boolean,
): Promise<ImportantDateRecord> {
  const item = await session.require(RecordType.ImportantDate, id);
  if (item.data.active === active) return item;
  return session.write(RecordType.ImportantDate, id, { ...item.data, active });
}

export async function deleteImportantDate(session: WriteSession, id: string): Promise<void> {
  await session.require(RecordType.ImportantDate, id);
  await session.write(RecordType.ImportantDate, id, null);
}
